import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLinkedin, 
  faGithub, 
  faXTwitter 
} from '@fortawesome/free-brands-svg-icons';

import jkAutoLogo from '../assets/jkauto-logo.png';

// Constants
const COMPANY_NAME = 'JK Automations LLC';
const CURRENT_YEAR = new Date().getFullYear();

// Social links data
const SOCIAL_LINKS = [
  {
    href: 'https://www.linkedin.com/company/jk-automations-llc/',
    icon: faLinkedin,
    label: 'LinkedIn'
  },
  {
    href: 'https://github.com/jk-automations',
    icon: faGithub,
    label: 'GitHub'
  },
  {
    href: 'https://twitter.com/jkautomations',
    icon: faXTwitter,
    label: 'X (Twitter)'
  }
] as const;

// Navigation links data
const NAV_LINKS = [
  { to: '/ai-solutions', label: 'Solutions' },
  { to: '/local-setups', label: 'Local Setups' },
  { to: '/integrations', label: 'Integrations' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' }
] as const;

const LEGAL_LINKS = [
  { to: '/privacy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' }
] as const;

const Footer: React.FC = () => {
  return (
    <footer className="footer">

      {/* Main Content: Shopify-style minimal top band */}
      <div className="footer-main-content">
        <div className="footer-minimal-grid">
          
          {/* Left: Brand */}
          <div className="footer-brand-minimal">
            <div className="footer-brand-logo">
              <img 
                src={jkAutoLogo} 
                alt={`${COMPANY_NAME} Logo`} 
                className="footer-logo-image-minimal"
                width="32"
                height="32"
                loading="lazy"
              />
              <div className="footer-brand-text">
                <span className="footer-brand-name-minimal">{COMPANY_NAME}</span>
                <span className="footer-brand-tagline-minimal">Automation that keeps busy small teams focused</span>
              </div>
            </div>
          </div>

          {/* Center: Compact nav */}
          <nav className="footer-nav-minimal" aria-label="Footer navigation">
            <ul className="footer-nav-list-minimal">
              {NAV_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="footer-nav-link-minimal">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Right: Legal */}
          <div className="footer-legal-minimal">
            <ul className="footer-legal-list-minimal">
              {LEGAL_LINKS.map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="footer-legal-link-minimal">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Far Right: Social */}
          <div className="footer-social-minimal">
            <div className="footer-social-links-inline" role="list" aria-label="Social media links">
              {SOCIAL_LINKS.map(({ href, icon, label }) => (
                <a 
                  key={href}
                  href={href} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="footer-social-link-inline"
                  aria-label={label}
                  role="listitem"
                >
                  <FontAwesomeIcon icon={icon} />
                </a>
              ))}
            </div>
          </div>
        </div>

      </div>


      {/* Bottom: Copyright */}
      <div className="footer-copyright-section">
        <p className="footer-copyright-text">
          © {CURRENT_YEAR} {COMPANY_NAME}. All rights reserved.
        </p>
      </div>
    </footer>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(Footer); 